import { Controller } from '@hotwired/stimulus'

import { SfLog } from '@app/adapters/logger'
import { deletePeriodSetRequest } from '@app/application/report/api/columns'
import {
  PERIOD_SET_ID_PARAM,
  REPORTS_SETTING_KEY,
} from '@app/application/report/constants'
import { trackPeriodSet } from '@app/application/report/period-set-tracking'
import { reportReloadStarted } from '@app/application/report/report-tracking'
import type { PeriodSetKind } from '@app/application/report/period-set-tracking'
import { useDropdown } from '@app/shared/pinia/dropdown'
import getPiniaInstance from '@app/shared/pinia/instance'
import useI18n from '@app/shared/vue/composables/use-I18n'
import { getSafeJSONValue, setSafeJSONValue } from '@app/utils/local-storage'

const I18n = useI18n()

// The delete button on the period sets a user saved, in the picker that
// lists them (reports/_period_set_picker.html.slim). Only rendered for a user
// who may delete one; the endpoint answers for that either way.
export default class extends Controller {
  static values = { reportId: Number, currentKind: String }

  declare reportIdValue: number

  declare currentKindValue: PeriodSetKind

  // The rows stay on screen until the navigation lands, so the same set can be
  // asked for twice. The second DELETE finds nothing and would tell the user the
  // set could not be deleted, about a set that was.
  private deleting = new Set<number>()

  // Everyone working on this client loses the set, so the confirm is the point.
  // The report is then read without asking for a set: whichever one it falls
  // back to is the server's answer, not ours.
  destroy(event: Event & { params: { id: number; name: string } }): void {
    event.preventDefault()

    const { id, name } = event.params

    if (this.deleting.has(id)) return

    if (
      !window.confirm(I18n.t('reports.period_sets.delete.confirm', { name }))
    ) {
      return
    }

    this.deleting.add(id)
    this.close()
    void this.deletePeriodSet(id)
  }

  // Every row navigates, so this fires on the way out and the report's own
  // properties are still those of the page the user left.
  switch(
    event: Event & { params: { kind: PeriodSetKind; selected: boolean } }
  ): void {
    this.close()

    // The row for the set already on screen still navigates, so it still has to
    // put the menu away. It just isn't a switch.
    if (event.params.selected) return

    trackPeriodSet('Switched', 'picker', {
      switched_from: this.currentKindValue,
      switched_to: event.params.kind,
    })
  }

  // Which dropdown is open is held in a store that outlives the page, and the
  // picker answers to the same id on the report it lands on, so a row that
  // navigates has to put it away on the way out.
  close(): void {
    useDropdown(getPiniaInstance()).closeDropdown()
  }

  private async deletePeriodSet(periodSetId: number): Promise<void> {
    try {
      await deletePeriodSetRequest({
        reportId: this.reportIdValue,
        periodSetId,
      })
    } catch (error) {
      this.deleting.delete(periodSetId)
      SfLog.error('Failed to delete the report period set', {
        error,
        extra: { reportId: this.reportIdValue, periodSetId },
      })
      alert(I18n.t('reports.period_sets.failed'))
      return
    }

    trackPeriodSet('Deleted', 'picker')

    this.forgetStoredPeriodSet(periodSetId)

    // Only the set that just went takes the reader with it. Deleting one they
    // were not reading leaves them where they are.
    const url = new URL(window.location.href)
    if (url.searchParams.get(PERIOD_SET_ID_PARAM) === String(periodSetId)) {
      url.searchParams.delete(PERIOD_SET_ID_PARAM)
    }

    // Like the save's reload: the reader asked to delete a set, not to open a
    // report.
    reportReloadStarted()
    window.Turbolinks.visit(url.toString())
  }

  // Dropping the param is not enough: reports--table-frame puts the stored id
  // back in the address bar on the next connect, and this one no longer exists.
  private forgetStoredPeriodSet(periodSetId: number): void {
    const key = `${REPORTS_SETTING_KEY}-${this.reportIdValue}`
    const settings = getSafeJSONValue<{ periodSetId?: string }>(key)

    if (settings?.periodSetId !== String(periodSetId)) return

    delete settings.periodSetId
    setSafeJSONValue(key, settings)
  }
}
